import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  Users, Calendar, ShoppingBag, Sparkles, TrendingUp, Star,
  BookOpen, Tag, DollarSign, Plus, Trash2, Check, Package, Pencil, Settings as SettingsIcon
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useSettings } from "@/context/SettingsContext";

export default function Admin() {
  const { user } = useAuth();
  const nav = useNavigate();
  const settings = useSettings();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [commissions, setCommissions] = useState(null);
  const [products, setProducts] = useState([]);
  const [showBlogForm, setShowBlogForm] = useState(false);
  const [showCatForm, setShowCatForm] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [editBlog, setEditBlog] = useState(null);
  const [editCat, setEditCat] = useState(null);
  const [blogForm, setBlogForm] = useState({ slug: "", title: "", excerpt: "", cover: "", author: "", read_time: "5 min", content: "" });
  const [catForm, setCatForm] = useState({ key: "", label: "", icon: "Sparkles", description: "" });
  const [settingsForm, setSettingsForm] = useState({});

  useEffect(() => {
    if (user === false) { nav("/login"); return; }
    if (!user) return;
    if (user.role !== "admin") { nav("/dashboard"); return; }
    loadAll();
  }, [user, nav]);

  // Prefill settings form on tab open
  useEffect(() => {
    if (tab === "settings") setSettingsForm({...settings});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const loadAll = async () => {
    try {
      const [s, u, b, c, cm, p] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/blogs"),
        api.get("/categories"),
        api.get("/admin/commissions"),
        api.get("/admin/products"),
      ]);
      setStats(s.data); setUsers(u.data); setBlogs(b.data);
      setCategories(c.data); setCommissions(cm.data); setProducts(p.data);
    } catch (e) { console.error(e); }
  };

  if (!stats) return <div className="p-16 text-center text-[#A0A4AB]">Loading admin console…</div>;

  const TABS = [
    { key: "overview", label: "Overview", icon: TrendingUp },
    { key: "users", label: "Users", icon: Users },
    { key: "commissions", label: "Commissions", icon: DollarSign },
    { key: "products", label: "Products", icon: Package },
    { key: "blogs", label: "Blogs (CMS)", icon: BookOpen },
    { key: "categories", label: "Categories", icon: Tag },
    { key: "settings", label: "Settings", icon: SettingsIcon },
  ];

  const Stat = ({ label, value, icon: Ic, accent }) => (
    <div className={`p-5 rounded-xl bg-[#0A0C10] border ${accent || "border-white/10"}`}>
      <Ic size={18} className="text-[#D4AF37]"/>
      <div className="font-serif text-2xl mt-3">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#A0A4AB] mt-1">{label}</div>
    </div>
  );

  const createBlog = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/blogs", blogForm);
      toast.success("Blog published");
      setShowBlogForm(false);
      setBlogForm({ slug: "", title: "", excerpt: "", cover: "", author: "", read_time: "5 min", content: "" });
      await loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const deleteBlog = async (id) => {
    if (!confirm("Delete this blog?")) return;
    await api.delete(`/admin/blogs/${id}`);
    toast.success("Deleted"); await loadAll();
  };
  const saveBlog = async () => {
    if (!editBlog) return;
    try {
      const { id, slug, title, excerpt, cover, author, read_time, content } = editBlog;
      await api.put(`/admin/blogs/${id}`, { slug, title, excerpt, cover, author, read_time, content });
      toast.success("Blog updated");
      setEditBlog(null); await loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const saveCat = async () => {
    if (!editCat) return;
    try {
      const { id, label, description, icon } = editCat;
      await api.put(`/admin/categories/${id}`, { label, description, icon });
      toast.success("Category updated");
      setEditCat(null); await loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const createCat = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/categories", catForm);
      toast.success("Category created");
      setShowCatForm(false);
      setCatForm({ key: "", label: "", icon: "Sparkles", description: "" });
      await loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const deleteCat = async (id) => {
    if (!confirm("Delete this category?")) return;
    await api.delete(`/admin/categories/${id}`);
    toast.success("Deleted"); await loadAll();
  };
  const approveProduct = async (pid) => {
    await api.post(`/admin/products/${pid}/approve`);
    toast.success("Approved"); await loadAll();
  };
  const removeProduct = async (pid) => {
    if (!confirm("Delete product?")) return;
    await api.delete(`/admin/products/${pid}`);
    toast.success("Deleted"); await loadAll();
  };

  const saveProduct = async () => {
    if (!editProduct) return;
    const { id, name, category, price, description, image, stock, approved } = editProduct;
    try {
      await api.put(`/admin/products/${id}`, {
        name, category, price: parseFloat(price), description, image,
        stock: parseInt(stock, 10), approved,
      });
      toast.success("Product updated");
      setEditProduct(null); await loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const saveSettings = async () => {
    try {
      const payload = {};
      Object.entries(settingsForm).forEach(([k, v]) => {
        if (v === undefined || v === null) return;
        if (typeof v === "boolean") { payload[k] = v; return; }
        if (String(v).length > 0) payload[k] = v;
      });
      if (payload.commission_pct !== undefined) payload.commission_pct = parseInt(payload.commission_pct, 10);
      await api.put("/admin/settings", payload);
      toast.success("Settings saved — reload to see changes across app");
      await settings.refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12 pb-24 lg:pb-12">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-2">Admin · CRM · CMS</div>
        <h1 className="font-serif text-4xl">Command Console</h1>
      </div>

      <div className="flex gap-2 border-b border-white/10 mb-8 overflow-x-auto">
        {TABS.map((t) => {
          const Ic = t.icon;
          return (
            <button
              key={t.key}
              data-testid={`admin-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-5 py-3 text-sm inline-flex items-center gap-2 border-b-2 transition whitespace-nowrap ${
                tab === t.key ? "border-[#D4AF37] text-[#D4AF37]"
                              : "border-transparent text-[#A0A4AB] hover:text-[#EAE6DF]"
              }`}
            >
              <Ic size={14}/> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "overview" && (
        <div className="grid md:grid-cols-4 lg:grid-cols-7 gap-3">
          <Stat label="Total Users" value={stats.users} icon={Users}/>
          <Stat label="Astrologers" value={stats.astrologers} icon={Star}/>
          <Stat label="Pandits" value={stats.pandits} icon={Sparkles}/>
          <Stat label="Bookings" value={stats.bookings} icon={Calendar}/>
          <Stat label="Orders" value={stats.orders} icon={ShoppingBag}/>
          <Stat label="Kundalis" value={stats.kundalis} icon={Sparkles}/>
          <Stat label="Revenue" value={`₹${(stats.revenue || 0).toLocaleString("en-IN")}`} icon={TrendingUp}/>
        </div>
      )}

      {tab === "users" && (
        <div className="p-6 rounded-xl bg-[#0A0C10] border border-white/10 overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead>
              <tr className="text-left border-b border-white/10">
                <th className="py-3 text-xs uppercase tracking-widest text-[#A0A4AB]">Name</th>
                <th className="py-3 text-xs uppercase tracking-widest text-[#A0A4AB]">Email</th>
                <th className="py-3 text-xs uppercase tracking-widest text-[#A0A4AB]">Role</th>
                <th className="py-3 text-xs uppercase tracking-widest text-[#A0A4AB]">Wallet</th>
                <th className="py-3 text-xs uppercase tracking-widest text-[#A0A4AB]">Referral</th>
                <th className="py-3 text-xs uppercase tracking-widest text-[#A0A4AB]">Joined</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-white/5 last:border-0">
                  <td className="py-3">{u.name}</td>
                  <td className="py-3 text-[#A0A4AB] text-xs">{u.email}</td>
                  <td className="py-3"><span className="px-2 py-1 rounded-full text-xs border border-white/20 text-[#D4AF37] capitalize">{u.role}</span></td>
                  <td className="py-3 text-[#D4AF37]">₹{(u.wallet_balance || 0).toFixed(0)}</td>
                  <td className="py-3 text-xs font-mono text-[#A0A4AB]">{u.referral_code || "—"}</td>
                  <td className="py-3 text-[#A0A4AB] text-xs">{new Date(u.created_at).toLocaleDateString("en-IN")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "commissions" && commissions && (
        <div className="space-y-6">
          <div className="grid md:grid-cols-4 gap-3">
            <Stat label="Bookings Gross" value={`₹${commissions.bookings_gross.toLocaleString("en-IN")}`} icon={DollarSign}/>
            <Stat label={`Commission (${commissions.commission_pct}%)`} value={`₹${commissions.bookings_commission.toLocaleString("en-IN")}`} icon={TrendingUp} accent="border-[#D4AF37]/40"/>
            <Stat label="Marketplace Revenue" value={`₹${commissions.orders_revenue.toLocaleString("en-IN")}`} icon={ShoppingBag}/>
            <Stat label="Subscriptions" value={`₹${commissions.subscriptions_revenue.toLocaleString("en-IN")}`} icon={Sparkles}/>
          </div>
          <div className="p-8 rounded-xl bg-gradient-to-br from-[#12151C] to-[#0A0C10] border border-[#D4AF37]/30">
            <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Total Platform Revenue</div>
            <div className="font-serif text-5xl gold-gradient mt-3">₹{commissions.total_platform_revenue.toLocaleString("en-IN")}</div>
            <div className="text-xs text-[#A0A4AB] mt-2">
              Commission + Marketplace + Subscription revenue combined.
              Provider payouts to date: ₹{commissions.provider_payouts.toLocaleString("en-IN")}
            </div>
          </div>
          <div className="p-6 rounded-xl bg-[#0A0C10] border border-white/10 overflow-x-auto">
            <div className="font-serif text-xl mb-4">Recent Bookings & Commissions</div>
            <table className="w-full text-sm min-w-[600px]">
              <thead>
                <tr className="text-left border-b border-white/10">
                  <th className="py-3 text-xs uppercase tracking-widest text-[#A0A4AB]">Customer</th>
                  <th className="py-3 text-xs uppercase tracking-widest text-[#A0A4AB]">Service</th>
                  <th className="py-3 text-xs uppercase tracking-widest text-[#A0A4AB]">Gross</th>
                  <th className="py-3 text-xs uppercase tracking-widest text-[#A0A4AB]">Commission</th>
                  <th className="py-3 text-xs uppercase tracking-widest text-[#A0A4AB]">Provider Net</th>
                </tr>
              </thead>
              <tbody>
                {commissions.recent_bookings.map((b) => (
                  <tr key={b.id} className="border-b border-white/5 last:border-0">
                    <td className="py-3">{b.customer_name}</td>
                    <td className="py-3 capitalize text-[#A0A4AB]">{b.service_type.replace("_", " ")}</td>
                    <td className="py-3">₹{b.amount}</td>
                    <td className="py-3 text-[#D4AF37]">₹{b.commission}</td>
                    <td className="py-3 text-emerald-400">₹{b.provider_earning}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "products" && (
        <div className="p-6 rounded-xl bg-[#0A0C10] border border-white/10">
          <div className="font-serif text-xl mb-4">All Products ({products.length})</div>
          <div className="grid md:grid-cols-3 gap-4">
            {products.map((p) => (
              <div key={p.id} className="p-4 rounded-lg border border-white/10">
                {p.image && <img src={p.image} className="w-full h-32 object-cover rounded mb-3"/>}
                <div className="text-sm truncate">{p.name}</div>
                <div className="text-xs text-[#D4AF37]">₹{p.price}</div>
                <div className="text-[10px] text-[#A0A4AB] mt-1">{p.vendor_name || "Platform"} · {p.category}</div>
                <div className="flex items-center gap-2 mt-3">
                  {p.approved ? (
                    <span className="text-[10px] px-2 py-1 rounded bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">Approved</span>
                  ) : (
                    <button data-testid={`approve-${p.id}`} onClick={() => approveProduct(p.id)} className="text-[10px] px-2 py-1 rounded bg-[#D4AF37] text-black inline-flex items-center gap-1">
                      <Check size={11}/> Approve
                    </button>
                  )}
                  <button data-testid={`edit-product-${p.id}`} onClick={() => setEditProduct({...p})}
                    className="text-[#D4AF37] hover:text-[#E5C865] ml-auto" title="Edit">
                    <Pencil size={13}/>
                  </button>
                  <button data-testid={`delete-product-${p.id}`} onClick={() => removeProduct(p.id)} className="text-[#FF5A36] hover:text-red-400">
                    <Trash2 size={13}/>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "blogs" && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <div className="font-serif text-xl">Blogs ({blogs.length})</div>
            <button data-testid="new-blog-btn" onClick={() => setShowBlogForm(true)} className="px-4 py-2 rounded-full bg-[#D4AF37] text-black text-sm inline-flex items-center gap-2 hover:bg-[#E5C865] transition">
              <Plus size={14}/> New Blog
            </button>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {blogs.map((b) => (
              <div key={b.id} className="p-5 rounded-xl bg-[#0A0C10] border border-white/10 flex gap-4">
                {b.cover && <img src={b.cover} className="w-24 h-24 object-cover rounded flex-none"/>}
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-[#D4AF37]">{b.author}</div>
                  <div className="text-sm mt-1 line-clamp-2">{b.title}</div>
                  <div className="text-xs text-[#A0A4AB] mt-1 line-clamp-1">{b.excerpt}</div>
                  <div className="mt-3 flex items-center gap-3">
                    <button data-testid={`edit-blog-${b.id}`} onClick={() => setEditBlog({...b})} className="text-xs text-[#D4AF37] hover:text-[#E5C865] inline-flex items-center gap-1">
                      <Pencil size={11}/> Edit
                    </button>
                    <button data-testid={`delete-blog-${b.id}`} onClick={() => deleteBlog(b.id)} className="text-xs text-[#FF5A36] hover:text-red-400 inline-flex items-center gap-1">
                      <Trash2 size={11}/> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "categories" && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <div className="font-serif text-xl">Service Categories ({categories.length})</div>
            <button data-testid="new-cat-btn" onClick={() => setShowCatForm(true)} className="px-4 py-2 rounded-full bg-[#D4AF37] text-black text-sm inline-flex items-center gap-2 hover:bg-[#E5C865] transition">
              <Plus size={14}/> New Category
            </button>
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            {categories.map((c) => (
              <div key={c.id} className="p-5 rounded-xl bg-[#0A0C10] border border-white/10">
                <div className="text-xs text-[#D4AF37] uppercase tracking-widest">{c.key}</div>
                <div className="font-serif text-lg mt-1">{c.label}</div>
                <div className="text-xs text-[#A0A4AB] mt-1">{c.description}</div>
                <button data-testid={`delete-cat-${c.id}`} onClick={() => deleteCat(c.id)} className="mt-3 text-xs text-[#FF5A36] hover:text-red-400 inline-flex items-center gap-1">
                  <Trash2 size={11}/> Delete
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "settings" && (
        <div className="grid md:grid-cols-2 gap-4" data-testid="settings-tab">
          <div className="p-6 rounded-xl bg-[#0A0C10] border border-white/10 space-y-3">
            <div className="font-serif text-xl mb-2">Brand & Contact</div>
            {[
              ["brand_name", "Brand Name"],
              ["tagline", "Tagline"],
              ["address_line1", "Address Line 1"],
              ["address_line2", "Address Line 2"],
              ["phone_1", "Phone 1"],
              ["phone_2", "Phone 2"],
              ["whatsapp_number", "WhatsApp Number (e.g. 918679994272)"],
              ["email", "Contact Email"],
            ].map(([k, l]) => (
              <div key={k}>
                <label className="text-xs text-[#A0A4AB]">{l}</label>
                <input data-testid={`settings-${k}`} value={settingsForm[k] || ""}
                  onChange={(e) => setSettingsForm({...settingsForm, [k]: e.target.value})}
                  className="mt-1 w-full px-3 py-2 rounded bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"/>
              </div>
            ))}
          </div>

          <div className="p-6 rounded-xl bg-[#0A0C10] border border-white/10 space-y-3">
            <div className="font-serif text-xl mb-2">Apps & Socials</div>
            {[
              ["android_url", "Google Play URL"],
              ["ios_url", "App Store URL"],
              ["instagram_url", "Instagram URL"],
              ["youtube_url", "YouTube URL"],
              ["twitter_url", "Twitter / X URL"],
              ["telegram_url", "Telegram URL"],
            ].map(([k, l]) => (
              <div key={k}>
                <label className="text-xs text-[#A0A4AB]">{l}</label>
                <input data-testid={`settings-${k}`} value={settingsForm[k] || ""}
                  onChange={(e) => setSettingsForm({...settingsForm, [k]: e.target.value})}
                  placeholder="https://…"
                  className="mt-1 w-full px-3 py-2 rounded bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"/>
              </div>
            ))}
            <div>
              <label className="text-xs text-[#A0A4AB]">Platform Commission %  (0-90)</label>
              <input data-testid="settings-commission_pct" type="number" min="0" max="90"
                value={settingsForm.commission_pct ?? 20}
                onChange={(e) => setSettingsForm({...settingsForm, commission_pct: e.target.value})}
                className="mt-1 w-full px-3 py-2 rounded bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"/>
              <div className="text-[10px] text-[#6E737D] mt-1">Applies to new bookings from now on.</div>
            </div>

            <button data-testid="settings-save-btn" onClick={saveSettings}
              className="w-full mt-4 py-3 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#E5C865] transition">
              Save Settings
            </button>
          </div>

          {/* Homepage sections toggle */}
          <div className="md:col-span-2 p-6 rounded-xl bg-[#0A0C10] border border-white/10">
            <div className="font-serif text-xl mb-2">Homepage Sections</div>
            <p className="text-xs text-[#A0A4AB] mb-4">Toggle which sections appear on the public homepage. Great for seasonal changes (Diwali, Kumbh, festivals).</p>
            <div className="grid md:grid-cols-3 gap-3">
              {[
                ["show_services", "Services Bento"],
                ["show_kundali_feature", "AI Kundali Feature"],
                ["show_live_events", "Live Events"],
                ["show_marketplace_preview", "Marketplace Preview"],
                ["show_testimonials", "Testimonials"],
                ["show_app_banner", "App Download Banner"],
                ["show_final_cta", "Final CTA (Create Account)"],
              ].map(([k, l]) => (
                <label key={k} className="flex items-center gap-3 p-3 rounded-lg border border-white/10 hover:border-white/20 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid={`settings-${k}`}
                    checked={settingsForm[k] ?? true}
                    onChange={(e) => setSettingsForm({...settingsForm, [k]: e.target.checked})}
                    className="w-4 h-4 accent-[#D4AF37]"
                  />
                  <span className="text-sm text-[#EAE6DF]">{l}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Edit product modal */}
      {editProduct && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-6 overflow-y-auto py-10" onClick={() => setEditProduct(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-[#0A0C10] border border-white/10 rounded-xl p-8 max-w-lg w-full space-y-3">
            <h3 className="font-serif text-2xl">Edit Product</h3>
            <input data-testid="ep-name" value={editProduct.name} onChange={(e)=>setEditProduct({...editProduct, name: e.target.value})}
              placeholder="Name" className="w-full px-3 py-2 rounded bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"/>
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="ep-category" value={editProduct.category} onChange={(e)=>setEditProduct({...editProduct, category: e.target.value})}
                placeholder="Category" className="w-full px-3 py-2 rounded bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"/>
              <input data-testid="ep-price" type="number" value={editProduct.price} onChange={(e)=>setEditProduct({...editProduct, price: e.target.value})}
                placeholder="Price" className="w-full px-3 py-2 rounded bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"/>
            </div>
            <input data-testid="ep-image" value={editProduct.image} onChange={(e)=>setEditProduct({...editProduct, image: e.target.value})}
              placeholder="Image URL" className="w-full px-3 py-2 rounded bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"/>
            <textarea data-testid="ep-desc" rows={3} value={editProduct.description} onChange={(e)=>setEditProduct({...editProduct, description: e.target.value})}
              placeholder="Description" className="w-full px-3 py-2 rounded bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"/>
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="ep-stock" type="number" value={editProduct.stock} onChange={(e)=>setEditProduct({...editProduct, stock: e.target.value})}
                placeholder="Stock" className="w-full px-3 py-2 rounded bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"/>
              <label className="flex items-center gap-2 text-sm text-[#A0A4AB]">
                <input type="checkbox" checked={!!editProduct.approved} onChange={(e)=>setEditProduct({...editProduct, approved: e.target.checked})}/>
                Approved (visible in shop)
              </label>
            </div>
            <div className="flex gap-3">
              <button data-testid="ep-save" onClick={saveProduct} className="flex-1 py-3 rounded-full bg-[#D4AF37] text-black font-medium">Save Changes</button>
              <button onClick={() => setEditProduct(null)} className="px-5 py-3 rounded-full border border-white/15">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit blog modal */}
      {editBlog && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-6 overflow-y-auto py-10" onClick={() => setEditBlog(null)}>
          <div onClick={(e)=>e.stopPropagation()} className="bg-[#0A0C10] border border-white/10 rounded-xl p-8 max-w-2xl w-full space-y-3">
            <h3 className="font-serif text-2xl">Edit Blog</h3>
            <input data-testid="eb-slug" value={editBlog.slug || ""} onChange={(e)=>setEditBlog({...editBlog,slug:e.target.value})} placeholder="slug" className="w-full px-3 py-2 rounded bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"/>
            <input data-testid="eb-title" value={editBlog.title || ""} onChange={(e)=>setEditBlog({...editBlog,title:e.target.value})} placeholder="Title" className="w-full px-3 py-2 rounded bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"/>
            <input data-testid="eb-cover" value={editBlog.cover || ""} onChange={(e)=>setEditBlog({...editBlog,cover:e.target.value})} placeholder="Cover image URL" className="w-full px-3 py-2 rounded bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"/>
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="eb-author" value={editBlog.author || ""} onChange={(e)=>setEditBlog({...editBlog,author:e.target.value})} placeholder="Author" className="w-full px-3 py-2 rounded bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"/>
              <input data-testid="eb-readtime" value={editBlog.read_time || ""} onChange={(e)=>setEditBlog({...editBlog,read_time:e.target.value})} placeholder="Read time" className="w-full px-3 py-2 rounded bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"/>
            </div>
            <textarea data-testid="eb-excerpt" rows={2} value={editBlog.excerpt || ""} onChange={(e)=>setEditBlog({...editBlog,excerpt:e.target.value})} placeholder="Excerpt" className="w-full px-3 py-2 rounded bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"/>
            <textarea data-testid="eb-content" rows={6} value={editBlog.content || ""} onChange={(e)=>setEditBlog({...editBlog,content:e.target.value})} placeholder="Content (Markdown)" className="w-full px-3 py-2 rounded bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"/>
            <div className="flex gap-3">
              <button data-testid="eb-save" onClick={saveBlog} className="flex-1 py-3 rounded-full bg-[#D4AF37] text-black font-medium">Save Changes</button>
              <button onClick={() => setEditBlog(null)} className="px-5 py-3 rounded-full border border-white/15">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit category modal */}
      {editCat && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-6" onClick={() => setEditCat(null)}>
          <div onClick={(e)=>e.stopPropagation()} className="bg-[#0A0C10] border border-white/10 rounded-xl p-8 max-w-md w-full space-y-3">
            <h3 className="font-serif text-2xl">Edit Category</h3>
            <div className="text-xs text-[#A0A4AB]">Key (immutable): <span className="font-mono text-[#D4AF37]">{editCat.key}</span></div>
            <input data-testid="ec-label" value={editCat.label || ""} onChange={(e)=>setEditCat({...editCat,label:e.target.value})} placeholder="Label" className="w-full px-3 py-2 rounded bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"/>
            <textarea data-testid="ec-desc" rows={2} value={editCat.description || ""} onChange={(e)=>setEditCat({...editCat,description:e.target.value})} placeholder="Description" className="w-full px-3 py-2 rounded bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"/>
            <div className="flex gap-3">
              <button data-testid="ec-save" onClick={saveCat} className="flex-1 py-3 rounded-full bg-[#D4AF37] text-black font-medium">Save Changes</button>
              <button onClick={() => setEditCat(null)} className="px-5 py-3 rounded-full border border-white/15">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Blog form modal */}
      {showBlogForm && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-6 overflow-y-auto py-10" onClick={() => setShowBlogForm(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={createBlog} className="bg-[#0A0C10] border border-white/10 rounded-xl p-8 max-w-2xl w-full space-y-3">
            <h3 className="font-serif text-2xl">New Blog Post</h3>
            <input required data-testid="blog-slug" placeholder="slug (e.g. my-post)" value={blogForm.slug} onChange={(e)=>setBlogForm({...blogForm,slug:e.target.value})} className="w-full px-3 py-2 rounded bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"/>
            <input required data-testid="blog-title" placeholder="Title" value={blogForm.title} onChange={(e)=>setBlogForm({...blogForm,title:e.target.value})} className="w-full px-3 py-2 rounded bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"/>
            <input required data-testid="blog-cover" placeholder="Cover image URL" value={blogForm.cover} onChange={(e)=>setBlogForm({...blogForm,cover:e.target.value})} className="w-full px-3 py-2 rounded bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"/>
            <div className="grid grid-cols-2 gap-3">
              <input required data-testid="blog-author" placeholder="Author" value={blogForm.author} onChange={(e)=>setBlogForm({...blogForm,author:e.target.value})} className="w-full px-3 py-2 rounded bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"/>
              <input data-testid="blog-readtime" placeholder="Read time" value={blogForm.read_time} onChange={(e)=>setBlogForm({...blogForm,read_time:e.target.value})} className="w-full px-3 py-2 rounded bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"/>
            </div>
            <textarea required data-testid="blog-excerpt" rows={2} placeholder="Short excerpt" value={blogForm.excerpt} onChange={(e)=>setBlogForm({...blogForm,excerpt:e.target.value})} className="w-full px-3 py-2 rounded bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"/>
            <textarea required data-testid="blog-content" rows={6} placeholder="Content (Markdown)" value={blogForm.content} onChange={(e)=>setBlogForm({...blogForm,content:e.target.value})} className="w-full px-3 py-2 rounded bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"/>
            <div className="flex gap-3">
              <button type="submit" data-testid="blog-submit" className="flex-1 py-3 rounded-full bg-[#D4AF37] text-black font-medium">Publish</button>
              <button type="button" onClick={() => setShowBlogForm(false)} className="px-5 py-3 rounded-full border border-white/15">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Category form modal */}
      {showCatForm && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-6" onClick={() => setShowCatForm(false)}>
          <form onClick={(e)=>e.stopPropagation()} onSubmit={createCat} className="bg-[#0A0C10] border border-white/10 rounded-xl p-8 max-w-md w-full space-y-3">
            <h3 className="font-serif text-2xl">New Category</h3>
            <input required data-testid="cat-key" placeholder="key (e.g. palmistry)" value={catForm.key} onChange={(e)=>setCatForm({...catForm,key:e.target.value})} className="w-full px-3 py-2 rounded bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"/>
            <input required data-testid="cat-label" placeholder="Label" value={catForm.label} onChange={(e)=>setCatForm({...catForm,label:e.target.value})} className="w-full px-3 py-2 rounded bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"/>
            <textarea data-testid="cat-desc" rows={2} placeholder="Description" value={catForm.description} onChange={(e)=>setCatForm({...catForm,description:e.target.value})} className="w-full px-3 py-2 rounded bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"/>
            <div className="flex gap-3">
              <button type="submit" data-testid="cat-submit" className="flex-1 py-3 rounded-full bg-[#D4AF37] text-black font-medium">Create</button>
              <button type="button" onClick={() => setShowCatForm(false)} className="px-5 py-3 rounded-full border border-white/15">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
